import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Gift, Loader2, Search, X } from 'lucide-react';
import {
  adminEntries,
  adminExportEntriesUrl,
  adminGetCompetition,
  adminGrantEntries,
  adminListGrantedEntries,
  adminSearchUsers,
} from '@/lib/api';
import { formatDateShort } from '@/lib/format';

// Local debounce so we don't hammer /admin/users/search on every keystroke.
const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const AdminEntries = () => {
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [comp, setComp] = useState(null);
  const [grants, setGrants] = useState([]);
  const [totalGranted, setTotalGranted] = useState(0);

  // Grant panel state
  const [userQ, setUserQ] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pickedUser, setPickedUser] = useState(null);
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState('');
  const [granting, setGranting] = useState(false);

  const debouncedQ = useDebounced(userQ, 300);

  const loadAll = useCallback(async () => {
    try {
      const [e, c, g] = await Promise.all([
        adminEntries(id),
        adminGetCompetition(id),
        adminListGrantedEntries(id),
      ]);
      setItems(e.items || []);
      setComp(c || null);
      setGrants(g.items || []);
      setTotalGranted(g.total_granted || 0);
    } catch (err) {
      // Keep the page usable even if one call fails.
      // eslint-disable-next-line no-console
      console.error('AdminEntries load failed', err);
    }
  }, [id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Live user search (debounced).
  useEffect(() => {
    let cancelled = false;
    if (debouncedQ.trim().length < 2) { setUserResults([]); return; }
    setSearching(true);
    adminSearchUsers(debouncedQ.trim())
      .then((r) => { if (!cancelled) setUserResults(r.items || []); })
      .catch(() => { if (!cancelled) setUserResults([]); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [debouncedQ]);

  const download = async () => {
    const url = adminExportEntriesUrl(id);
    const token = localStorage.getItem('pd_admin_token');
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `entries-${id}.csv`;
    link.click();
  };

  const remaining = useMemo(() => {
    if (!comp) return null;
    return Math.max(0, (comp.total_tickets || 0) - (comp.tickets_sold || 0));
  }, [comp]);

  const canGrant = pickedUser && qty >= 1 && qty <= (remaining ?? 0) && reason.trim().length >= 5 && !granting;

  const grant = async () => {
    if (!canGrant) return;
    setGranting(true);
    try {
      const r = await adminGrantEntries(id, {
        user_id: pickedUser.id,
        quantity: Number(qty),
        reason: reason.trim(),
      });
      toast.success(`Granted ${r.granted_count} entr${r.granted_count === 1 ? 'y' : 'ies'} to ${pickedUser.email}`);
      setPickedUser(null);
      setUserQ('');
      setUserResults([]);
      setQty(1);
      setReason('');
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Grant failed');
    } finally {
      setGranting(false);
    }
  };

  return (
    <div data-testid="admin-entries-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/competitions" className="text-xs text-muted-sage" data-testid="back-to-competitions">&larr; Competitions</Link>
          <h1 className="font-display text-4xl mt-2">{comp?.title || 'Entries'}</h1>
          <div className="font-mono text-xs text-muted-sage mt-1">
            {items.length} entries {comp ? (
              <>&middot; <span className="text-[color:var(--pd-text)]">{comp.tickets_sold ?? 0}</span> / {comp.total_tickets ?? 0} sold {totalGranted ? <>&middot; <span className="text-[color:var(--pd-gold)]">{totalGranted} granted</span></> : null}</>
            ) : null}
          </div>
        </div>
        <button onClick={download} className="bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-xl px-4 py-2 font-medium" data-testid="admin-export-csv">Export CSV</button>
      </div>

      {/* -------------------------- GRANT ENTRIES PANEL -------------------- */}
      <div className="mt-6 bg-surface border border-pd rounded-2xl p-5" data-testid="admin-grant-panel">
        <div className="flex items-start gap-3">
          <Gift size={18} className="text-[color:var(--pd-gold)] mt-1 shrink-0" />
          <div className="flex-1">
            <div className="font-mono text-[10px] text-muted-sage uppercase">Creator / partner gifting</div>
            <h2 className="font-display text-2xl mt-1">Grant free entries</h2>
            <p className="text-xs text-muted-sage mt-1 max-w-2xl">
              Look up an existing account by email and gift them entries for this competition. Granted entries sit in the exact same draw pool as paid tickets (identical odds), count against the ticket cap, and are excluded from revenue. This is separate from the public free postal entry route &mdash; use this only for creator / partner shoutouts and record the reason on every grant.
            </p>
          </div>
        </div>

        {/* User picker */}
        <div className="mt-5">
          <label className="block text-[11px] text-muted-sage font-mono uppercase mb-1">Search customer by email</label>
          {pickedUser ? (
            <div className="flex items-center justify-between rounded-xl border border-[color:var(--pd-accent-yellow)]/50 bg-[color:var(--pd-bg)] px-4 py-3" data-testid="grant-picked-user">
              <div>
                <div className="text-sm text-[color:var(--pd-text)]">{pickedUser.full_name || '(no name)'}</div>
                <div className="text-xs font-mono text-muted-sage">{pickedUser.email}</div>
              </div>
              <button onClick={() => setPickedUser(null)} className="text-muted-sage hover:text-[color:var(--pd-text)]" data-testid="grant-clear-user"><X size={16} /></button>
            </div>
          ) : (
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-sage" />
              <input
                type="text"
                placeholder={"Start typing an email\u2026"}
                value={userQ}
                onChange={(e) => setUserQ(e.target.value)}
                className="w-full bg-[color:var(--pd-bg)] border border-pd rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[color:var(--pd-accent-yellow)]"
                data-testid="grant-user-search-input"
              />
              {userResults.length > 0 && (
                <div className="mt-2 rounded-xl border border-pd bg-[color:var(--pd-bg)] overflow-hidden max-h-60 overflow-y-auto" data-testid="grant-user-results">
                  {userResults.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => { setPickedUser(u); setUserResults([]); setUserQ(''); }}
                      className="w-full text-left px-4 py-2.5 hover:bg-[color:var(--pd-bg-2)] border-b border-pd last:border-b-0"
                      data-testid={`grant-user-result-${u.id}`}
                    >
                      <div className="text-sm text-[color:var(--pd-text)]">{u.full_name || '(no name)'}</div>
                      <div className="text-xs font-mono text-muted-sage">{u.email}</div>
                    </button>
                  ))}
                </div>
              )}
              {searching && userResults.length === 0 && debouncedQ.trim().length >= 2 && (
                <div className="mt-2 text-xs text-muted-sage inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Searching&hellip;</div>
              )}
              {!searching && debouncedQ.trim().length >= 2 && userResults.length === 0 && (
                <div className="mt-2 text-xs text-muted-sage">No accounts match &ldquo;{debouncedQ}&rdquo;.</div>
              )}
            </div>
          )}
        </div>

        {/* Quantity + reason */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-[140px_1fr_auto] gap-3 items-end">
          <div>
            <label className="block text-[11px] text-muted-sage font-mono uppercase mb-1">Quantity</label>
            <input
              type="number"
              min={1}
              max={Math.max(1, remaining ?? 1)}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="w-full bg-[color:var(--pd-bg)] border border-pd rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-[color:var(--pd-accent-yellow)]"
              data-testid="grant-quantity"
            />
            <div className="text-[10px] text-muted-sage mt-1">
              {remaining !== null ? <>{remaining} available on this comp</> : '\u2014'}
            </div>
          </div>
          <div>
            <label className="block text-[11px] text-muted-sage font-mono uppercase mb-1">Reason (recorded on every entry)</label>
            <input
              type="text"
              placeholder={"e.g. Instagram story shoutout \u2014 @thepadelblondie"}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full bg-[color:var(--pd-bg)] border border-pd rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--pd-accent-yellow)]"
              data-testid="grant-reason"
            />
            <div className={`text-[10px] mt-1 ${reason.trim().length >= 5 ? 'text-muted-sage' : 'text-[color:var(--pd-danger)]'}`}>
              {reason.trim().length >= 5 ? 'Looks good.' : `Min 5 characters (currently ${reason.trim().length}).`}
            </div>
          </div>
          <div>
            <button
              onClick={grant}
              disabled={!canGrant}
              className="inline-flex items-center gap-2 bg-[color:var(--pd-gold)] text-[#14251c] rounded-xl px-4 py-2.5 text-sm font-medium disabled:opacity-50"
              data-testid="grant-submit"
              title={!pickedUser ? 'Pick an account first' : (reason.trim().length < 5 ? 'Reason too short' : (qty > (remaining ?? 0) ? 'Not enough capacity' : ''))}
            >
              {granting ? <Loader2 size={14} className="animate-spin" /> : <Gift size={14} />}
              Grant {qty || 0} entr{Number(qty) === 1 ? 'y' : 'ies'}
            </button>
          </div>
        </div>

        {qty > (remaining ?? 0) && (
          <div className="mt-3 text-xs text-[color:var(--pd-danger)]" data-testid="grant-cap-warning">
            Cap exceeded: only {remaining} ticket(s) left on this competition.
          </div>
        )}
      </div>

      {/* -------------------------- GRANT LOG ------------------------------ */}
      {grants.length > 0 && (
        <div className="mt-6" data-testid="admin-grant-log">
          <div className="font-mono text-[10px] text-muted-sage uppercase mb-2">Granted entries log</div>
          <div className="overflow-x-auto border border-pd rounded-2xl">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] text-muted-sage uppercase">
                  <th className="p-3">When</th>
                  <th className="p-3">Account</th>
                  <th className="p-3">Qty</th>
                  <th className="p-3">Reason</th>
                  <th className="p-3">Tickets</th>
                  <th className="p-3">Granted by</th>
                </tr>
              </thead>
              <tbody>
                {grants.map((g) => (
                  <tr key={g.id} className="border-t border-pd align-top" data-testid={`admin-grant-row-${g.id}`}>
                    <td className="p-3 text-xs text-muted-sage whitespace-nowrap">{formatDateShort(g.granted_at)}</td>
                    <td className="p-3">
                      <div className="text-sm">{g.user_full_name || '(no name)'}</div>
                      <div className="text-xs font-mono text-muted-sage">{g.user_email}</div>
                    </td>
                    <td className="p-3 font-mono">{g.quantity}</td>
                    <td className="p-3 text-sm">{g.reason}</td>
                    <td className="p-3 font-mono text-xs text-muted-sage max-w-[240px] break-all">{(g.ticket_numbers || []).join(', ')}</td>
                    <td className="p-3 text-xs font-mono text-muted-sage">{g.granted_by || '\u2014'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* -------------------------- ALL ENTRIES TABLE ---------------------- */}
      <div className="mt-6 overflow-x-auto border border-pd rounded-2xl">
        <table className="w-full text-sm" data-testid="admin-entries-table">
          <thead>
            <tr className="text-left font-mono text-[10px] text-muted-sage uppercase">
              <th className="p-3">Ticket</th>
              <th className="p-3">Email</th>
              <th className="p-3">Name</th>
              <th className="p-3">Source</th>
              <th className="p-3">Paid</th>
              <th className="p-3">Skill</th>
              <th className="p-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.ticket_number} className="border-t border-pd">
                <td className="p-3 font-mono text-xs">{e.ticket_number}</td>
                <td className="p-3">{e.email}</td>
                <td className="p-3">{e.full_name || '\u2014'}</td>
                <td className="p-3 font-mono text-xs">
                  <span className={`inline-block rounded-md px-2 py-0.5 text-[10px] uppercase ${e.source === 'granted' ? 'bg-[color:var(--pd-gold)]/15 text-[color:var(--pd-gold)]' : e.source === 'cashflows' ? 'bg-[color:var(--pd-accent-yellow)]/10 text-[color:var(--pd-accent-yellow)]' : e.source === 'postal' ? 'bg-white/5 text-muted-sage' : 'bg-white/5 text-muted-sage'}`}>{e.source}</span>
                </td>
                <td className="p-3">{e.paid ? 'Yes' : 'No'}</td>
                <td className="p-3">{e.skill_correct ? 'OK' : '\u2013'}</td>
                <td className="p-3 text-xs text-muted-sage">{formatDateShort(e.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
export default AdminEntries;
